/**
 * Hardcoded Min/Max trigger derivation for the two Bio-Medical derived KPIs.
 *
 * These derived KPIs have no trigger rows of their own, so their targets are
 * derived from other questions with a FIXED mapping (kept here, isolated, so it
 * is easy to find and remove if the backend ever computes these generically):
 *
 *   - 10145 "Total Bio-Medical Waste": owned entirely by the backend (set-value
 *     else its master formula); the client never computes it.
 *   - 10146 "Per Bed Per Day": always a client override — the colour-trigger sum
 *     EXCLUDING Cytotoxic (9908-9911) divided by (Approved Beds (9934) actual ×
 *     days in the selected month). The override is unconditional: if Approved
 *     Beds (9934) ever gets its own trigger the backend may synthesize a
 *     (wrongly-bounded) 10146, but the client always overwrites it here, so the
 *     backend-owned Total (10145) and client-owned Per-Bed (10146) never drift.
 *
 * Cytotoxic (9912) is excluded from the per-bed aggregate by design; it still
 * renders as its own colour row with its own trigger.
 */
import { computeDifference, computeRemark } from "./helpers";

const PER_BED_QID = 10146;
const AGGREGATE_COLOUR_QIDS = [9908, 9909, 9910, 9911];
const APPROVED_BEDS_QID = 9934;

/**
 * Analytics questionIds that must be fetched (but are NOT rendered as rows) so
 * the maths above can run — the hook adds these to the analytics request.
 */
export const BIOMEDICAL_DERIVED_EXTRA_QIDS = [APPROVED_BEDS_QID];

/** Coerce to a finite number, or null. */
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** Sum a trigger key across sub-question rows, ignoring missing values; null if none present. */
const sumKey = (rowByQid, qids, key) => {
  let total = 0;
  let any = false;
  qids.forEach((qid) => {
    const v = toNum(rowByQid[qid] && rowByQid[qid][key]);
    if (v !== null) {
      total += v;
      any = true;
    }
  });
  return any ? total : null;
};

/** Set a row's min/max and recompute its difference/remark against the new max. */
const setTrigger = (row, min, max) => {
  if (!row) return;
  row.min = min;
  row.max = max;
  row.difference = computeDifference(row.actual, max);
  row.remark = computeRemark(row.actual, max);
};

/**
 * Fill in Min/Max for the biomedical derived rows (10145, 10146) in place.
 *
 * Only 10146 "Per Bed Per Day" is computed here (10145 is backend-owned): the
 * colour-trigger sum excluding Cytotoxic (9908-9911) divided by (Approved Beds
 * (9934) actual × days in the selected month).
 *
 * @param {Array} rows  the built rows for one source
 * @param {Object} analyticsByQid  String(qid) -> analytics row (for Approved Beds actual)
 * @param {Function} triggerFor  qid -> { min, max } trigger for this scope/month (unused here)
 * @param {number|null} daysInMonth  calendar days in the selected month
 */
export const applyBiomedicalDerivedTriggers = (
  rows,
  analyticsByQid,
  triggerFor,
  daysInMonth
) => {
  const rowByQid = {};
  rows.forEach((r) => {
    rowByQid[r.questionId] = r;
  });

  const totalMin = sumKey(rowByQid, AGGREGATE_COLOUR_QIDS, "min");
  const totalMax = sumKey(rowByQid, AGGREGATE_COLOUR_QIDS, "max");

  const approvedBeds = toNum(
    (analyticsByQid[String(APPROVED_BEDS_QID)] || {}).selected_date_range_value
  );
  const days = toNum(daysInMonth);
  const denominator =
    approvedBeds !== null && approvedBeds !== 0 && days !== null && days !== 0
      ? approvedBeds * days
      : null;
  const perBed = (v) =>
    v !== null && denominator !== null ? v / denominator : null;
  setTrigger(rowByQid[PER_BED_QID], perBed(totalMin), perBed(totalMax));
};

const WATER_PER_BED_QID = 10147;
const WATER_NUMERATOR_QID = 10053;

/**
 * Extra analytics questionIds the water derived-trigger maths needs (Approved
 * Beds actual), fetched but not rendered.
 */
export const WATER_DERIVED_EXTRA_QIDS = [APPROVED_BEDS_QID];

/**
 * Fill in Min/Max for the water derived row (10147) in place: the trigger of
 * 10053 divided by the actual (selected-range) value of Approved Beds (9934).
 * 10053 isn't a rendered row, so its trigger is looked up via `triggerFor`.
 * @param {Array} rows  the built rows for one source
 * @param {Object} analyticsByQid  String(qid) -> analytics row (for 9934's actual)
 * @param {Function} triggerFor  qid -> { min, max } trigger for this source/month
 */
export const applyWaterDerivedTriggers = (rows, analyticsByQid, triggerFor) => {
  const rowByQid = {};
  rows.forEach((r) => {
    rowByQid[r.questionId] = r;
  });

  const numerator = triggerFor(WATER_NUMERATOR_QID) || {};
  const min = toNum(numerator.min);
  const max = toNum(numerator.max);
  const beds = toNum(
    (analyticsByQid[String(APPROVED_BEDS_QID)] || {}).selected_date_range_value
  );
  const perBed = (v) =>
    v !== null && beds !== null && beds !== 0 ? v / beds : null;
  setTrigger(rowByQid[WATER_PER_BED_QID], perBed(min), perBed(max));
};
